import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { doc, getDoc, setDoc, getDocs, collection } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  LinearProgress,
  Tabs,
  Tab,
  MenuItem,
  Select,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  ListSubheader,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import BusinessIcon from '@mui/icons-material/Business';

function newRow() {
  return {
    id: Date.now().toString() + Math.random().toString(36).slice(2),
    userId: '',
    name: '',
    role: '',
    budgetedHours: '',
    hourlyRate: '',
  };
}

function calcBudgetValue(row) {
  const h = parseFloat(row.budgetedHours) || 0;
  const r = parseFloat(row.hourlyRate) || 0;
  return h * r;
}

function fmtSEK(val) {
  return Number(val).toLocaleString('sv-SE') + ' kr';
}

function progressColor(pct) {
  if (pct > 100) return '#d32f2f';
  if (pct >= 80) return '#f9a825';
  return '#2e7d32';
}

export default function ProjectBudget() {
  const { offerId } = useParams();
  const navigate = useNavigate();

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [offerTitle, setOfferTitle] = useState('');
  const [rows, setRows] = useState([newRow()]);
  const [milltimeImports, setMilltimeImports] = useState([]);
  const [importing, setImporting] = useState(false);

  // Departments + users
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [activeDeptIds, setActiveDeptIds] = useState([]);
  const [addDeptOpen, setAddDeptOpen] = useState(false);
  const [pendingDeptId, setPendingDeptId] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [offerSnap, budgetSnap, deptSnap, userSnap] = await Promise.all([
        getDoc(doc(db, 'offers', offerId)),
        getDoc(doc(db, 'budgets', offerId)),
        getDocs(collection(db, 'departments')),
        getDocs(collection(db, 'users')),
      ]);

      const allDepts = deptSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const allUsers = userSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setDepartments(allDepts);
      setUsers(allUsers);

      let offerDeptIds = [];
      if (offerSnap.exists()) {
        const od = offerSnap.data();
        setOfferTitle(od.title || '');
        offerDeptIds = od.departmentIds || [];
      }

      if (budgetSnap.exists()) {
        const data = budgetSnap.data();
        setRows(data.rows && data.rows.length > 0 ? data.rows : [newRow()]);
        setMilltimeImports(data.milltimeImports || []);
        // Merge offer depts + any extra depts saved in the budget doc
        const savedExtra = data.extraDeptIds || [];
        const merged = [...new Set([...offerDeptIds, ...savedExtra])];
        setActiveDeptIds(merged.length > 0 ? merged : offerDeptIds);
      } else {
        setActiveDeptIds(offerDeptIds);
      }
    } catch (err) {
      console.error('Fel vid laddning av budget:', err);
    } finally {
      setLoading(false);
    }
  }, [offerId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  async function handleSave() {
    setSaving(true);
    try {
      // Load original offer dept ids to compute extra
      const offerSnap = await getDoc(doc(db, 'offers', offerId));
      const offerDeptIds = offerSnap.exists() ? (offerSnap.data().departmentIds || []) : [];
      const extraDeptIds = activeDeptIds.filter(id => !offerDeptIds.includes(id));

      await setDoc(
        doc(db, 'budgets', offerId),
        {
          offerId,
          offerTitle,
          rows,
          milltimeImports,
          extraDeptIds,
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );
    } catch (err) {
      console.error('Fel vid sparande:', err);
    } finally {
      setSaving(false);
    }
  }

  function handleRowChange(id, field, value) {
    setRows(prev =>
      prev.map(r => (r.id === id ? { ...r, [field]: value } : r))
    );
  }

  function handleUserSelect(rowId, userId) {
    const user = users.find(u => u.id === userId);
    setRows(prev => prev.map(r => {
      if (r.id !== rowId) return r;
      return {
        ...r,
        userId,
        name: user ? (user.displayName || user.name || user.email || '') : '',
        role: user ? (user.title || '') : '',
      };
    }));
  }

  function handleAddRow() {
    setRows(prev => [...prev, newRow()]);
  }

  function handleDeleteRow(id) {
    setRows(prev => prev.filter(r => r.id !== id));
  }

  function handleAddDept() {
    if (!pendingDeptId) return;
    setActiveDeptIds(prev => [...new Set([...prev, pendingDeptId])]);
    setPendingDeptId('');
    setAddDeptOpen(false);
  }

  function handleRemoveDept(deptId) {
    setActiveDeptIds(prev => prev.filter(id => id !== deptId));
  }

  function handleFileUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    const reader = new FileReader();
    reader.onload = async evt => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName =
          workbook.SheetNames.find(n =>
            n.toLowerCase().includes('projecttime')
          ) || workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonRows = XLSX.utils.sheet_to_json(sheet, { defval: '' });

        const entries = jsonRows.map(r => ({
          name: String(r['Namn'] || r['Name'] || '').trim(),
          date: String(r['Datum'] || r['Date'] || '').trim(),
          activity: String(r['Aktivitet'] || r['Activity'] || '').trim(),
          note: String(r['Notering'] || r['Note'] || '').trim(),
          hours: parseFloat(r['Antal'] || r['Hours'] || 0) || 0,
        }));

        const importRecord = {
          importedAt: new Date().toISOString(),
          fileName: file.name,
          entries,
        };

        const updatedImports = [...milltimeImports, importRecord];
        setMilltimeImports(updatedImports);

        await setDoc(
          doc(db, 'budgets', offerId),
          {
            offerId,
            offerTitle,
            rows,
            milltimeImports: updatedImports,
            updatedAt: new Date().toISOString(),
          },
          { merge: true }
        );
      } catch (err) {
        console.error('Fel vid import av Milltime-rapport:', err);
      } finally {
        setImporting(false);
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  }

  // Aggregate utfall per name from all imports
  function getUtfallPerName() {
    const map = {};
    for (const imp of milltimeImports) {
      for (const entry of imp.entries || []) {
        const key = entry.name;
        if (!key) continue;
        map[key] = (map[key] || 0) + (entry.hours || 0);
      }
    }
    return map;
  }

  const utfallMap = getUtfallPerName();

  // Budget totals
  const totalBudgetedHours = rows.reduce(
    (s, r) => s + (parseFloat(r.budgetedHours) || 0),
    0
  );
  const totalBudgetedValue = rows.reduce((s, r) => s + calcBudgetValue(r), 0);

  // Users belonging to active departments, grouped by dept
  const activeDepts = departments.filter(d => activeDeptIds.includes(d.id));
  const availableUsers = users.filter(u => activeDeptIds.includes(u.departmentId));

  // Departments not yet active (for add-dept dialog)
  const inactiveDepts = departments.filter(d => !activeDeptIds.includes(d.id));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate('/offers')} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 700, flexGrow: 1 }}>
          Projektbudget{offerTitle ? ` — ${offerTitle}` : ''}
        </Typography>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{ bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' } }}
        >
          {saving ? 'Sparar…' : 'Spara'}
        </Button>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} indicatorColor="primary" textColor="primary">
          <Tab label="Budget" />
          <Tab label="Utfall" />
          <Tab label="Jämförelse" />
        </Tabs>
      </Paper>

      {/* Panel 1: Budget */}
      {tab === 0 && (
        <Paper sx={{ p: 3 }}>
          {/* Department chips + add button */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5, flexWrap: 'wrap' }}>
            <BusinessIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mr: 0.5 }}>
              Affärsområden:
            </Typography>
            {activeDepts.map(d => (
              <Chip
                key={d.id}
                label={d.offerCode ? `${d.offerCode} – ${d.name}` : d.name}
                size="small"
                onDelete={activeDepts.length > 1 ? () => handleRemoveDept(d.id) : undefined}
                sx={{ bgcolor: '#f0f0f0' }}
              />
            ))}
            {activeDepts.length === 0 && (
              <Typography variant="caption" color="text.disabled">Inga affärsområden kopplade</Typography>
            )}
            {inactiveDepts.length > 0 && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setAddDeptOpen(true)}
                sx={{ ml: 0.5, fontSize: '0.75rem' }}
              >
                Affärsområde
              </Button>
            )}
          </Box>

          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Budgetposter
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>Resurs</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 140 }}>Roll</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Budgeterade timmar</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Timpris (SEK)</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Budgeterat värde (SEK)</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(row => (
                <TableRow key={row.id}>
                  <TableCell>
                    <FormControl variant="standard" fullWidth size="small">
                      <Select
                        value={row.userId || ''}
                        onChange={e => handleUserSelect(row.id, e.target.value)}
                        displayEmpty
                        renderValue={val => {
                          if (!val) return <span style={{ color: '#aaa' }}>Välj resurs…</span>;
                          const u = users.find(u => u.id === val);
                          return u ? (u.displayName || u.name || u.email) : row.name || val;
                        }}
                      >
                        <MenuItem value=""><em>– Välj resurs –</em></MenuItem>
                        {activeDepts.map(dept => {
                          const deptUsers = availableUsers.filter(u => u.departmentId === dept.id);
                          if (!deptUsers.length) return null;
                          return [
                            <ListSubheader key={`hdr-${dept.id}`} sx={{ fontSize: '0.7rem', lineHeight: '28px', bgcolor: '#fafafa' }}>
                              {dept.offerCode ? `${dept.offerCode} – ${dept.name}` : dept.name}
                            </ListSubheader>,
                            ...deptUsers.map(u => (
                              <MenuItem key={u.id} value={u.id} sx={{ pl: 3 }}>
                                <Box>
                                  <Typography sx={{ fontSize: '0.875rem' }}>
                                    {u.displayName || u.name || u.email}
                                  </Typography>
                                  {u.title && (
                                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                      {u.title}
                                    </Typography>
                                  )}
                                </Box>
                              </MenuItem>
                            )),
                          ];
                        })}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <TextField
                      variant="standard"
                      value={row.role}
                      onChange={e => handleRowChange(row.id, 'role', e.target.value)}
                      placeholder="Roll"
                      size="small"
                      fullWidth
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      variant="standard"
                      value={row.budgetedHours}
                      onChange={e => handleRowChange(row.id, 'budgetedHours', e.target.value)}
                      placeholder="0"
                      size="small"
                      inputProps={{ style: { textAlign: 'right' } }}
                      sx={{ width: 80 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      variant="standard"
                      value={row.hourlyRate}
                      onChange={e => handleRowChange(row.id, 'hourlyRate', e.target.value)}
                      placeholder="0"
                      size="small"
                      inputProps={{ style: { textAlign: 'right' } }}
                      sx={{ width: 90 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{fmtSEK(calcBudgetValue(row))}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleDeleteRow(row.id)} disabled={rows.length === 1}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {/* Total row */}
              <TableRow sx={{ bgcolor: '#fafafa' }}>
                <TableCell colSpan={2}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Totalt</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{totalBudgetedHours} tim</Typography>
                </TableCell>
                <TableCell />
                <TableCell align="right">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmtSEK(totalBudgetedValue)}</Typography>
                </TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
          <Button startIcon={<AddIcon />} onClick={handleAddRow} sx={{ mt: 2 }} size="small">
            Lägg till rad
          </Button>
        </Paper>
      )}

      {/* Panel 2: Utfall */}
      {tab === 1 && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Utfall (Milltime-import)</Typography>
            <Button
              variant="outlined"
              component="label"
              startIcon={importing ? <CircularProgress size={16} /> : <UploadFileIcon />}
              disabled={importing}
            >
              {importing ? 'Importerar…' : 'Importera Milltime-rapport (.xls/.xlsx)'}
              <input type="file" accept=".xls,.xlsx" hidden onChange={handleFileUpload} />
            </Button>
          </Box>

          {utfallSummary(utfallMap).length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Timmar per person (alla importer)</Typography>
              <Table size="small" sx={{ mb: 4 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Namn</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Totalt (tim)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {utfallSummary(utfallMap).map(row => (
                    <TableRow key={row.name}>
                      <TableCell>{row.name}</TableCell>
                      <TableCell align="right">{row.hours.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Tidigare importer</Typography>
          {milltimeImports.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Inga importer ännu.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Datum</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Filnamn</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Antal rader</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {milltimeImports.map((imp, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{new Date(imp.importedAt).toLocaleString('sv-SE')}</TableCell>
                    <TableCell>{imp.fileName}</TableCell>
                    <TableCell align="right">{(imp.entries || []).length}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}

      {/* Panel 3: Jämförelse */}
      {tab === 2 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Jämförelse — Budget vs Utfall</Typography>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Namn</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Budgeterat (tim)</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Utfall (tim)</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Kvar (tim)</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>Förbrukat (%)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(row => {
                const budgeted = parseFloat(row.budgetedHours) || 0;
                const actual = utfallMap[row.name] || 0;
                const remaining = budgeted - actual;
                const pct = budgeted > 0 ? (actual / budgeted) * 100 : 0;
                const color = progressColor(pct);
                return (
                  <TableRow key={row.id}>
                    <TableCell>{row.name || '—'}</TableCell>
                    <TableCell align="right">{budgeted}</TableCell>
                    <TableCell align="right">{actual.toFixed(2)}</TableCell>
                    <TableCell align="right" sx={{ color: remaining < 0 ? '#d32f2f' : 'inherit' }}>
                      {remaining.toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <LinearProgress variant="determinate" value={Math.min(pct, 100)} sx={{
                            height: 8, borderRadius: 4, bgcolor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 4 },
                          }} />
                        </Box>
                        <Typography variant="caption" sx={{ minWidth: 42, color, fontWeight: 600 }}>
                          {pct.toFixed(0)}%
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
              {/* Total row */}
              {(() => {
                const totalActual = rows.reduce((s, r) => s + (utfallMap[r.name] || 0), 0);
                const totalRemaining = totalBudgetedHours - totalActual;
                const totalPct = totalBudgetedHours > 0 ? (totalActual / totalBudgetedHours) * 100 : 0;
                const color = progressColor(totalPct);
                return (
                  <TableRow sx={{ bgcolor: '#fafafa' }}>
                    <TableCell><Typography variant="body2" sx={{ fontWeight: 600 }}>Totalt</Typography></TableCell>
                    <TableCell align="right"><Typography variant="body2" sx={{ fontWeight: 600 }}>{totalBudgetedHours}</Typography></TableCell>
                    <TableCell align="right"><Typography variant="body2" sx={{ fontWeight: 600 }}>{totalActual.toFixed(2)}</Typography></TableCell>
                    <TableCell align="right" sx={{ color: totalRemaining < 0 ? '#d32f2f' : 'inherit' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{totalRemaining.toFixed(2)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <LinearProgress variant="determinate" value={Math.min(totalPct, 100)} sx={{
                            height: 8, borderRadius: 4, bgcolor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 4 },
                          }} />
                        </Box>
                        <Typography variant="caption" sx={{ minWidth: 42, color, fontWeight: 600 }}>
                          {totalPct.toFixed(0)}%
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })()}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Add department dialog */}
      <Dialog open={addDeptOpen} onClose={() => setAddDeptOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Lägg till affärsområde</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Affärsområde</InputLabel>
            <Select
              value={pendingDeptId}
              label="Affärsområde"
              onChange={e => setPendingDeptId(e.target.value)}
            >
              {inactiveDepts.map(d => (
                <MenuItem key={d.id} value={d.id}>
                  {d.offerCode ? `${d.offerCode} – ${d.name}` : d.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAddDeptOpen(false); setPendingDeptId(''); }}>Avbryt</Button>
          <Button variant="contained" onClick={handleAddDept} disabled={!pendingDeptId}
            sx={{ bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' } }}>
            Lägg till
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function utfallSummary(utfallMap) {
  return Object.entries(utfallMap).map(([name, hours]) => ({ name, hours }));
}
